// AppsPage.js
import React, {useEffect, useState, useRef, useMemo} from 'react';
import {db} from '../firebase';
import {deleteFileFromWasabi, uploadFileToWasabi} from '../utils/wasabiUploader';
import {FiEdit, FiImage, FiSmartphone, FiTrash2, FiUpload, FiStar, FiCheckCircle} from 'react-icons/fi';
import {toast, ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import IconPreview from "../utils/PictureHelper";
import {ref, set, onValue, remove, get, update} from 'firebase/database';
import { onValueThrottled } from '../utils/rtdbThrottle';
import JSZip, {forEach} from 'jszip';


export const toSafeKey = (str) => str.replaceAll('.', '_');

/**
 * Convert a Base64 string (with or without the data URI prefix) to a File object.
 *
 * @param base64     - The Base64 string. Accepts both “data:image/png;base64,…” or raw Base64.
 * @param filename   - Desired filename, e.g. "icon.png".
 * @param mimeType   - MIME type, e.g. "image/png".  If omitted, it’s inferred from a data URI prefix or
 *                     falls back to "application/octet-stream".
 */
export function base64ToFile(base64, filename, mimeType) {
  // 1. Strip the data-URL prefix if present  ("data:image/png;base64,")
  const [, header, data] =
  base64.match(/^data:(.*?);base64,(.*)$/) ?? ["", "", base64];

  const type = mimeType || header || "application/octet-stream";

  // 2. Decode Base64 → binary string → Uint8Array
  const binary = atob(data);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);

  // 3. Create the File
  return new File([bytes], filename, { type });
}

export function formatBytes(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
}

const parseApkWithWorker = file =>
    new Promise((resolve, reject) => {
      const worker = new Worker('/apkParserWorker.js');
      worker.onmessage = ({ data }) => {
        let iconFile;
        const {appName, packageName, version, versionName, icon, iconName, error} = data;
        if (error) return reject(new Error(error));

        iconFile = base64ToFile(icon, iconName);

        resolve({appName, packageName, version, versionName, iconFile});
        worker.terminate();
      }
      worker.onerror = e => {
        reject(new Error(e.message));
        worker.terminate();
      };
      worker.postMessage(file);
    });


export default function AppsPage() {
  const apkInputRef   = useRef(null);
  const iconInputRef  = useRef(null);

  const [apkSize, setApkSize] = useState(0);
  const [apkFile, setApkFile]     = useState(null);
  const [imageFile, setImageFile] = useState(null);

  const [version, setVersion] = useState(''); // Version is manually entered
  const [versionName, setVersionName] = useState('')
  const [appName, setAppName] = useState('');
  const [packageName, setPackageName] = useState(''); // New state for package name
  const [devices, setDevices] = useState({});
  const [appList, setAppList] = useState([]);

  const [uploading, setUploading] = useState(false);
  const [editUploading, setEditUploading] = useState(false);

  const [loadingMetadata, setLoadingMetadata] = useState(false);

  const [editingApp, setEditingApp]         = useState(null); // the row you clicked
  const [editAppName, setEditAppName]       = useState('');
  const [editPackageName, setEditPackageName] = useState('');    // new dotted name
  const [editVersion, setEditVersion]       = useState('');
  const [editVersionName, setEditVersionName]       = useState('');

  const [editApkSize, setEditApkSize] = useState(0);
  const [editApkFile, setEditApkFile]       = useState(null);   // new .apk
  const [editImageFile, setEditImageFile]   = useState(null);   // new icon file

  const [loadingEditMetadata, setLoadingEditMetadata] = useState(false);

  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [deleteAppCandidate, setDeleteAppCandidate] = useState(null);

  useEffect(() => {
    const appsRef = ref(db, 'mdm/app_store');
    const devicesRef = ref(db, 'mdm/devices');

    const unsubscribeApps = onValue(appsRef, (snap) => {
      const data = snap.val() || {};
      const flat = [];

      Object.entries(data).forEach(([pkgKey, pkgData]) => {
        const {
          originalPackageName,
          name: appName,
          iconUrl,
          latestVersion,
          latestVersionCode,
          self_update_enabled: selfUpdateFlag,
          versions = {}
        } = pkgData;
        // Kill-switch: absent = enabled (existing packages keep working untouched).
        const selfUpdateEnabled = selfUpdateFlag !== false;

        Object.entries(versions).forEach(([verKey, verMeta]) => {
          // "Latest" = the RELEASE pointer devices act on (SelfUpdateManager / Updater watchdog).
          // latestVersionCode is authoritative (unique); latestVersion by name is the legacy fallback.
          const isLatest = latestVersionCode != null
            ? String(latestVersionCode) === String(verKey)
            : verMeta.versionName === latestVersion;
          flat.push({
            pkgKey,                // safe key
            originalPackageName,   // dotted: "com.example.app"
            appName,               // human name
            iconUrl,               // URL/string
            latestVersion,         // released versionName (legacy / display)
            latestVersionCode,     // released versionCode (what devices install)
            selfUpdateEnabled,     // package-level kill-switch
            version: verKey,       // versionCode key, e.g. "71"
            downloadUrl:    verMeta.downloadUrl,
            versionName:    verMeta.versionName,
            timestamp:      verMeta.timestamp,
            isLatest
          });
        });
      });

      setAppList(flat);
    });

    const unsubscribeDevs = onValueThrottled(devicesRef, (snap) => {
      const data = snap.val() || {};
      setDevices(data);
    })

    return () => {
      unsubscribeApps()
      unsubscribeDevs()
    }
  }, []);

  const handleApkFileChange = async (e) => {
    let file = e.target.files[0];
    if (!file) return;

    if (file.name.toLowerCase().endsWith('.xapk')) {
      const arrayBuffer = await file.arrayBuffer();
      const zip = await JSZip.loadAsync(arrayBuffer);
      // Find the first .apk entry
      const apkEntry = Object.keys(zip.files).find(f => f.toLowerCase().endsWith('.apk'));
      if (!apkEntry) {
        toast.error('No APK found inside that XAPK!');
        return;
      }
      const apkBlob = await zip.file(apkEntry).async('blob');
      file = new File(
          [apkBlob],
          file.name.replace(/\.xapk$/i, '.apk'),
          { type: 'application/vnd.android.package-archive' }
      );
    }

    setLoadingMetadata(true);
    setApkFile(file);

    try {
      const result = await parseApkWithWorker(file);
      const { appName, packageName, version, versionName, iconFile } = result;


      if (iconFile) {
        const reader = new FileReader();
        reader.readAsDataURL(iconFile);
      }

      setAppName(appName);
      setPackageName(packageName);
      setVersion(version);
      setVersionName(versionName)
      setImageFile(iconFile);

      toast.info(`App name: ${appName || 'unknown'}`);
      packageName
          ? toast.info(`Package: ${packageName}`)
          : toast.warn('Package name missing. Please fill it in.');
      version
          ? toast.info(`Version code: ${version}`)
          : toast.warn('Version code missing. Please fill it in.');
      versionName
          ? toast.info(`Version name: ${versionName}`)
          : toast.warn('Version name missing. Please fill it in.');
      iconFile
          ? toast.info('Icon extracted successfully.')
          : toast.warn('Icon not found. You can upload one manually.');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to process APK.';
      toast.error(msg);

      setAppName('');
      setPackageName('');
      setVersion('');
      setVersionName('')
      setImageFile(null);
      setApkSize(0)
      if (apkInputRef.current) {
        apkInputRef.current.value = '';
      }
    } finally {
      setLoadingMetadata(false);
      setApkSize(file.size);
    }
  };

  useEffect(() => {
    return () => imageFile && URL.revokeObjectURL(imageFile);
  }, [imageFile]);

  // Handle manual image upload (optional override)
  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
    }
  };

  async function fetchAndUpdateAppsToWatch(newPackageName) {
    const appsToWatchRef = ref(db, 'mdm/apps_to_watch');

    try {
      const snap = await get(appsToWatchRef);
      const currentValue = snap.exists() ? snap.val() : '';

      const watchList = currentValue
          .split(',')
          .map(s => s.trim())
          .filter(Boolean); // removes empty strings

      if (!watchList.includes(newPackageName)) {
        watchList.push(newPackageName);
        await set(appsToWatchRef, watchList.join(', '));
        // console.log('apps_to_watch updated successfully');
      } else {
        // console.log('Package already in apps_to_watch');
      }
    } catch (error) {
      console.error('Failed to fetch/update apps_to_watch:', error.message);
    }
  }


  const handleUpload = async () => {
    if (!apkFile || !version || !versionName || !appName || !packageName) {
      toast.error('Please fill all fields and select an APK file.');
      return;
    }

    const safePackage = encodeURIComponent(toSafeKey(packageName));
    const pkgRef      = ref(db, `mdm/app_store/${safePackage}`);

    // 1) Load existing data (if any)
    let pkgSnap, pkgData;
    let iconUrl = '', apkUrl = '';
    try {
      pkgSnap  = await get(pkgRef);
      pkgData  = pkgSnap.exists()
          ? pkgSnap.val()
          : {
            originalPackageName: packageName,  // dotted
            name:                appName,
            iconUrl:             iconUrl,
            versions:            {},           // versions as an object
          };
    } catch (err) {
      toast.error('Could not load package data: ' + err.message);
      return;
    }

    // Ensure versions is at least an object
    if (typeof pkgData.versions !== 'object' || pkgData.versions === null) {
      pkgData.versions = {};
    }

    // 2) Prevent duplicate version
    if (pkgData.versions[version]) {
      toast.error(`Version “${version}” for “${packageName}” already exists.`);
      return;
    }

    // 2.1) Prevent duplicate version name
    const versionExists = Object.values(pkgData.versions || {}).some(
        (v) => v.versionName === versionName
    );

    if (versionExists) {
      toast.error(`Version name “${versionName}” for “${packageName}” already exists.`);
      return;
    }

    const apkPath = `apk/${safePackage}-${version}-${Date.now()}`;
    const iconPath = imageFile ? `apk_images/${safePackage}-${Date.now()}` : null;

    // Construct paths

    // Upload APK
    setUploading(true);

    const apkUpload = await uploadFileToWasabi(apkFile, apkPath);
    if (!apkUpload?.publicUrl) {
      toast.error('APK upload failed')
      return;
    }
    apkUrl = apkUpload.publicUrl

    // Upload Icon (optional)
    let iconUpload = null;
    if (imageFile) {
      iconUpload = await uploadFileToWasabi(imageFile, iconPath);
      if (!iconUpload?.publicUrl) {
        toast.error('Icon upload failed')
        return;
      }
    }
    pkgData.iconUrl = iconUpload?.publicUrl;

    setUploading(false);


    // 3) Add the new version under its safe key
    pkgData.versions[version] = {
      versionName:     versionName,
      downloadUrl:     apkUrl,
      timestamp:       Date.now(),
    };

    // 4) Write *all* pkgData back in one shot
    try {
      await set(pkgRef, pkgData);

      await fetchAndUpdateAppsToWatch(packageName)

      // 5) Clear form
      setApkFile(null);
      setImageFile(null);
      setApkSize(0)
      if (apkInputRef.current)  apkInputRef.current.value  = '';
      if (iconInputRef.current) iconInputRef.current.value = '';
      setVersion('');
      setVersionName('');
      setAppName('');
      setPackageName('');

      toast.success(`APK "${appName}" v${version} uploaded.`);
    } catch (err) {
      setUploading(false);
      toast.error('Failed to upload APK: ' + err.message);
    }
  };

  // Set a version as the latest = RELEASE it. Devices (monitoring SelfUpdateManager, updater
  // watchdog) install only the version pointed to here - uploading alone changes nothing on the
  // fleet. latestVersionCode is what devices resolve; latestVersion (name) is kept for display
  // and for older device builds that still match by name.
  const setAsLatestVersion = async (row) => {
    try {
      const appRef = ref(db, `mdm/app_store/${row.pkgKey}`);
      await update(appRef, {
        latestVersion: row.versionName,
        latestVersionCode: Number(row.version),
      });
      toast.success(`Released ${row.appName} v${row.versionName} (code ${row.version}) — devices will self-update to it`);
    } catch (err) {
      toast.error('Failed to set latest version: ' + err.message);
    }
  };

  // Package-level kill-switch: OFF makes every device ignore the release pointer immediately.
  const toggleSelfUpdate = async (row) => {
    const next = !row.selfUpdateEnabled;
    try {
      const appRef = ref(db, `mdm/app_store/${row.pkgKey}`);
      await update(appRef, { self_update_enabled: next });
      toast.success(next
        ? `Self-update ON for ${row.appName}`
        : `Self-update OFF for ${row.appName} — devices will not install anything until turned back on`);
    } catch (err) {
      toast.error('Failed to toggle self-update: ' + err.message);
    }
  };

  const groupedApps = useMemo(() => {
    return appList.reduce((acc, row) => {
      const pkg = row.originalPackageName;
      if (!acc[pkg]) acc[pkg] = [];
      acc[pkg].push(row);
      return acc;
    }, {});
  }, [appList]);

  // Open edit modal
  const openEditModal = (row) => {
    setEditingApp(row);

    // app-level
    setEditAppName(row.appName);

    // version-level
    setEditPackageName(row.originalPackageName);
    setEditVersion(row.version);
    setEditVersionName(row.versionName);

    // clear any pending files
    setEditApkFile(null);
    setEditImageFile(null);
  };

  // Handle edit APK file change
  const handleEditApkFileChange = async (e) => {
    let file = e.target.files[0];
    if (!file) return;

    // If it’s an XAPK, unzip and extract the first .apk
    if (file.name.toLowerCase().endsWith('.xapk')) {
      const buf     = await file.arrayBuffer();
      const zip     = await JSZip.loadAsync(buf);
      const entry   = Object.keys(zip.files).find(f => f.toLowerCase().endsWith('.apk'));
      if (!entry) {
        toast.error('No APK inside that XAPK!');
        return;
      }
      const apkBlob    = await zip.file(entry).async('blob');
      file = new File(
          [apkBlob],
          file.name.replace(/\.xapk$/i, '.apk'),
          { type: 'application/vnd.android.package-archive' }
      );
    }

    setLoadingEditMetadata(true);
    setEditApkFile(file);

    try {
        const result = await parseApkWithWorker(file);
        const { appName, packageName, version, versionName, iconFile } = result;


        if (iconFile) {
          const reader = new FileReader();
          reader.readAsDataURL(iconFile);
        }

        setEditVersion(version);
        setEditVersionName(versionName);
        setEditImageFile(iconFile);

        version
            ? toast.info(`Version code: ${version}`)
            : toast.warn('Version code missing. Please fill it in.');
        versionName
            ? toast.info(`Version name: ${versionName}`)
            : toast.warn('Version name missing. Please fill it in.');
        iconFile
            ? toast.info('Icon extracted successfully.')
            : toast.warn('Icon not found. You can upload one manually.');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to process APK.';
        toast.error(msg);

        setEditAppName('');
        setEditPackageName('');
        setEditVersion('');
        setEditVersionName('');
        setEditImageFile(null);
        setEditApkSize(0)
      } finally {
        setLoadingEditMetadata(false);
        setEditApkSize(file.size);
      }
  };

  // Handle edit image file change
  const handleEditImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditImageFile(file);
    }
  };

  // Save edited app data
  const saveEditedApp = async () => {
    if (!editingApp) return;

    // safe keys
    const oldPkgKey = toSafeKey(editingApp.originalPackageName.trim());
    const newPkgKey = toSafeKey(editPackageName.trim());
    const oldVerKey = editingApp.version;
    const newVerKey = editVersion;
    const newVerKeyStr = String(newVerKey);
    const oldVerKeyStr = String(oldVerKey)

    try {

      const oldSnap = await get(ref(db, `mdm/app_store/${oldPkgKey}`));

      const pkgData = oldSnap.val()

      // Ensure versions is at least an object
      if (typeof pkgData.versions !== 'object' || pkgData.versions === null) {
        pkgData.versions = {};
      }

      // 2) Prevent duplicate version
      if (pkgData.versions[newVerKeyStr] && newVerKeyStr !== oldVerKeyStr) {
        toast.error(`Version “${newVerKey}” for “${editPackageName}” already exists.`);
        return;
      }

      // 2.1) Prevent duplicate version name
      const versionExists = Object.values(pkgData.versions || {}).some(
          (v) => v.versionName === editVersionName && editVersionName !== pkgData.versions[oldVerKey].versionName
      );

      if (versionExists) {
        toast.error(`Version name “${editVersionName}” for “${editPackageName}” already exists.`);
        return;
      }
      let finalIconUrl = ''
      let finalDownloadUrl = ''

      let packageKey = ''
      let versionKey = ''

      if (oldPkgKey !== newPkgKey) {
        packageKey = newPkgKey;
      } else {
        packageKey = oldPkgKey;
      }
      if (oldVerKey !== newVerKey) {
        versionKey = newVerKey;
      } else {
        versionKey = oldVerKey;
      }

      setEditUploading(true)
        // 1) upload new icon if any
      if(editImageFile){
        const imageUploadPath = `apk_images/${packageKey}-${Date.now()}`
        const {publicUrl} = await uploadFileToWasabi(editImageFile, imageUploadPath);
        finalIconUrl = publicUrl;

        if (editingApp.iconUrl)
          await deleteFileFromWasabi(editingApp.iconUrl);
      } else {
        finalIconUrl = editingApp?.iconUrl;
      }

      // 2) upload new APK if any
      if(editApkFile){
        const apkUploadPath = `apk/${packageKey}-${versionKey}-${Date.now()}`
        const {publicUrl} = await uploadFileToWasabi(editApkFile, apkUploadPath);
        finalDownloadUrl = publicUrl;

        if (editingApp.downloadUrl) await deleteFileFromWasabi(editingApp.downloadUrl);
      } else {
        finalDownloadUrl = editingApp?.downloadUrl;
      }


      setEditUploading(false)
      // ——————————————————————————————————————————————————————————
      // 3) MOVE / RENAME the whole app if package name changed
      if (oldPkgKey !== newPkgKey) {
        const oldSnap = await get(ref(db, `mdm/app_store/${oldPkgKey}`));
        const { originalPackageName, name, iconUrl, versions = {} } = oldSnap.val();

        // write under the new key, preserving ALL versions
        await set(ref(db, `mdm/app_store/${newPkgKey}`), {
          originalPackageName: editPackageName,
          name:                editAppName,
          iconUrl:             finalIconUrl,
          versions,            // object of version_key → { downloadUrl, timestamp, … }
        });

        // delete the old node
        await remove(ref(db, `mdm/app_store/${oldPkgKey}`));
      } else {
        // just update the app‐level metadata in place
        await update(ref(db, `mdm/app_store/${oldPkgKey}`), {
          originalPackageName: editPackageName,
          name:                editAppName,
          iconUrl:             finalIconUrl,
        });
      }

      const pkgKey = newPkgKey; // from here on use the target key

      // ——————————————————————————————————————————————————————————
      // 4) HANDLE VERSION KEY CHANGES & UPDATES

      // if the version string changed, delete the old child
      if (oldVerKey !== newVerKey) {
        await remove(ref(db, `mdm/app_store/${pkgKey}/versions/${oldVerKey}`));
      }

      // now upsert the single version you edited
      await update(ref(db, `mdm/app_store/${pkgKey}/versions`), {
        [newVerKey]: {
          versionName:     editVersionName,
          downloadUrl:     finalDownloadUrl,
          timestamp:       Date.now(),
        }
      });

      // If the row being edited IS the released one, keep the release pointer in step. Without
      // this the pointer keeps the OLD versionName (or points at a version key that no longer
      // exists), and since the pointer is what the fleet and the update indicators read, the
      // dashboard would compare against a stale string while devices happily follow
      // latestVersionCode. The "Set as Latest" button is hidden on the released row, so there
      // would be no way to repair it from the UI either.
      const releasedSnap = await get(ref(db, `mdm/app_store/${pkgKey}`));
      const released = releasedSnap.val() || {};
      const wasReleased = released.latestVersionCode != null
        ? String(released.latestVersionCode) === String(oldVerKey)
        : released.latestVersion === editingApp?.versionName;
      if (wasReleased) {
        await update(ref(db, `mdm/app_store/${pkgKey}`), {
          latestVersion: editVersionName,
          latestVersionCode: Number(newVerKey),
        });
      }

      toast.success('App and version updated successfully!');
      setEditingApp(null);
    } catch (err) {
      setEditUploading(false);
      toast.error('Failed to save changes: ' + err.message);
    }
  };

  async function deleteVersion(app) {
    const { originalPackageName, version, appName } = app;
    toast.info(`Deleting version ${version} of ${appName}…`);
    try {
      const safePkg = encodeURIComponent(toSafeKey(originalPackageName.trim()));
      const safeVer = version
      const verRef  = ref(db, `mdm/app_store/${safePkg}/versions/${safeVer}`);

      // Get the version data to access the APK URL
      const versionSnap = await get(verRef);
      const versionData = versionSnap.val();

      if (!versionData) {
        toast.error("Version data not found.");
        return;
      }

      const apkUrl = versionData.downloadUrl;
      if (apkUrl) {
        await deleteFileFromWasabi(apkUrl);
      }
      // remove just that version
      await remove(verRef);

      // If the deleted version was the released one, clear the pointer. Devices then do NOTHING
      // (they never fall back to "highest uploaded") until a version is marked as Latest again.
      const pkgNodeRef  = ref(db, `mdm/app_store/${safePkg}`);
      const pkgNodeSnap = await get(pkgNodeRef);
      const pkgNode     = pkgNodeSnap.val();
      const wasReleased = pkgNode && (
        pkgNode.latestVersionCode != null
          ? String(pkgNode.latestVersionCode) === String(version)
          : pkgNode.latestVersion === versionData.versionName
      );
      if (wasReleased) {
        await update(pkgNodeRef, { latestVersion: null, latestVersionCode: null });
        toast.info(`The deleted version was the released one — no release is marked for ${appName} now. Set another version as Latest.`);
      }

      // if no versions remain, remove the package node
      const versionsRef = ref(db, `mdm/app_store/${safePkg}/versions`);
      const snap = await get(versionsRef);
      if (!snap.exists()) {
        const apkRef = ref(db, `mdm/app_store/${safePkg}`);
        const apkSnap = await get(apkRef);
        const apkData = apkSnap.val();

        if(apkData && apkData.iconUrl) {
          await deleteFileFromWasabi(apkData.iconUrl);
        }

        await remove(ref(db, `mdm/app_store/${safePkg}`));
        toast.success(`As ${version} was only version, ${appName} was also removed.`);
      }
      else {
        toast.success(`Version "${version}" removed.`);
      }
    } catch (err) {
      toast.error('Failed to delete version: ' + (err.message || 'Unknown error'));
    }
  }

// 2) Delete entire app (all versions)
  async function deletePackage({ pkgKey, appName }) {
    toast.info(`Deleting all versions of ${appName}…`);

    try {
      const pkgRef = ref(db, `mdm/app_store/${pkgKey}`);
      const pkgSnap = await get(pkgRef);
      const pkgData = pkgSnap.val();

      if (pkgData) {
        // console.log("package data", pkgData)
        // Delete icon if exists
        if (pkgData.iconUrl) {
          await deleteFileFromWasabi(pkgData.iconUrl);
        }

        // Delete all version files if versions exist
        if (pkgData.versions) {
          for (const [versionName, versionData] of Object.entries(pkgData.versions)) {
            if (versionData.downloadUrl) {
              // console.log("deletion of version", versionData);
              await deleteFileFromWasabi(versionData.downloadUrl);
            }
          }
        }
      }

      await remove(pkgRef);
      toast.success(`App "${appName}" and all its versions removed.`);
    } catch (err) {
      toast.error('Failed to delete app: ' + (err.message || 'Unknown error'));
    }
  }

  const handleClearEditState = () => {
    setEditingApp(null)
    setEditApkFile(null)
    setEditImageFile(null)
    setEditApkSize(0)
    setEditAppName('')
    setEditPackageName('')
    setEditVersion('')
    setEditVersionName('')
  }

  return (
    <div className="page-shell">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />

      {/* Page header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="brand-icon-badge"><FiSmartphone /></div>
        <h1 className="text-xl font-semibold text-white">Apps Management</h1>
      </div>

      {/* Upload */}
      <section className="card-chrome mb-4">
        <header className="flex items-center gap-3 mb-4">
          <div className="brand-icon-badge"><FiUpload /></div>
          <h2 className="text-sm font-semibold text-white">Upload New App</h2>
        </header>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left — text inputs */}
          <div className="flex-1 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1.5">App Name</label>
                <input
                  type="text"
                  placeholder="e.g. MusicDJ"
                  value={appName}
                  onChange={(e) => setAppName(e.target.value)}
                  disabled={loadingMetadata}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1.5">Package Name</label>
                <input
                  type="text"
                  placeholder="e.g. rs.musicdj.player"
                  value={packageName}
                  onChange={(e) => setPackageName(e.target.value)}
                  disabled={loadingMetadata}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1.5">Version Code</label>
                <input
                  type="number"
                  inputMode="numeric"
                  placeholder="e.g. 123456789"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1.5">Version Name</label>
                <input
                  type="text"
                  placeholder="e.g. 1.2.3"
                  value={versionName}
                  onChange={(e) => setVersionName(e.target.value)}
                  className="input-field"
                />
              </div>
            </div>
          </div>

          {/* Right — icon, file pickers, primary action */}
          <div className="flex flex-col items-center justify-center lg:w-1/3 space-y-3">
            <div className="w-24 h-24 rounded-lg bg-surface-inset border border-white/[0.04] flex items-center justify-center">
              <IconPreview file={imageFile} className="w-20 h-20 object-contain rounded" />
            </div>

            <label className="btn-secondary cursor-pointer w-full max-w-xs">
              <FiImage /> Upload Icon
              <input
                ref={iconInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageFileChange}
                className="hidden"
              />
            </label>

            <label className="btn-secondary cursor-pointer w-full max-w-xs">
              <FiUpload /> Choose .apk / .xapk
              <input
                ref={apkInputRef}
                type="file"
                accept=".apk,.xapk"
                onChange={handleApkFileChange}
                className="hidden"
              />
            </label>

            {apkFile && !loadingMetadata && (
              <div className="text-xs text-gray-400 space-y-0.5 text-center">
                <p>Selected: <span className="text-white font-medium">{apkFile.name}</span></p>
                <p>Size: <span className="text-white font-medium">{formatBytes(apkSize)}</span></p>
              </div>
            )}

            <button
              onClick={handleUpload}
              disabled={loadingMetadata || uploading}
              className="btn-primary w-full max-w-xs"
            >
              {uploading ? 'Uploading…' : (loadingMetadata ? 'Extracting metadata…' : 'Upload App')}
            </button>
          </div>
        </div>
      </section>

      {/* App List */}
      <section className="card-chrome overflow-hidden p-0">
        <div className="w-full overflow-x-auto">
          <table className="min-w-full text-sm text-white">
            <thead className="bg-surface-inset text-xs uppercase tracking-wider text-gray-400">
              <tr>
                <th className="p-3 text-left font-medium">Icon</th>
                <th className="p-3 text-left font-medium">App Name</th>
                <th className="p-3 text-left font-medium">Version Code</th>
                <th className="p-3 text-left font-medium">Version Name</th>
                <th className="p-3 text-left font-medium">URL</th>
                <th className="p-3 text-left font-medium">Uploaded</th>
                <th className="p-3 text-left font-medium">Actions</th>
              </tr>
            </thead>

            {Object.entries(groupedApps).map(([pkgName, rows]) => (
              <tbody key={pkgName} className="border-t border-white/[0.04]">
                {rows.map((row, i) => {
                  return (
                    <tr
                      key={`${row.pkgKey}-${row.version}`}
                      className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors"
                    >
                      {i === 0 && (
                        <>
                          <td rowSpan={rows.length} className="p-3">
                            {row.iconUrl ? (
                              <IconPreview
                                file={row.iconUrl}
                                alt={row.appName}
                                className="w-12 h-12 object-contain rounded"
                              />
                            ) : (
                              <FiImage className="text-gray-500 w-8 h-8" />
                            )}
                          </td>
                          <td rowSpan={rows.length} className="p-3 text-gray-200">
                            {row.appName}
                          </td>
                        </>
                      )}

                      <td className="p-3 text-gray-300">{row.version}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-200">{row.versionName}</span>
                          {row.isLatest && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 text-xs font-medium">
                              <FiCheckCircle className="w-3 h-3" /> Latest
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="p-3">
                        <a
                          href={row.downloadUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-brand-400 hover:text-brand text-xs font-medium"
                        >
                          Download
                        </a>
                      </td>

                      <td className="p-3 text-xs text-gray-400 whitespace-nowrap">
                        {row.timestamp
                          ? new Date(row.timestamp).toLocaleString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '—'}
                      </td>

                      <td className="p-3">
                        <div className="flex flex-col gap-1.5">
                          {row.isLatest && (
                            <button
                              onClick={() => setAsLatestVersion(row)}
                              title="Rewrite the release pointer from this row (use it if latestVersion/latestVersionCode drifted, e.g. after editing this version)"
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/[0.05] border border-white/[0.08] text-gray-200 hover:bg-white/[0.08] text-xs font-medium transition-colors"
                            >
                              <FiStar /> Re-apply
                            </button>
                          )}
                          {row.isLatest && (
                            <button
                              onClick={() => toggleSelfUpdate(row)}
                              title="Kill-switch for this package: when OFF, devices ignore the released version and install nothing"
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                                row.selfUpdateEnabled
                                  ? 'bg-green-500/15 text-green-400 hover:bg-green-500/25'
                                  : 'bg-red-500/15 text-red-400 hover:bg-red-500/25'
                              }`}
                            >
                              Self-update: {row.selfUpdateEnabled ? 'ON' : 'OFF'}
                            </button>
                          )}
                          {!row.isLatest && (
                            <button
                              onClick={() => setAsLatestVersion(row)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-green-500/15 text-green-400 hover:bg-green-500/25 text-xs font-medium transition-colors"
                            >
                              <FiStar /> Set as Latest
                            </button>
                          )}
                          <button
                            onClick={() => openEditModal(row)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/[0.05] border border-white/[0.08] text-gray-200 hover:bg-white/[0.08] text-xs font-medium transition-colors"
                          >
                            <FiEdit /> Edit
                          </button>
                          <button
                            onClick={() => setDeleteCandidate({
                              originalPackageName: row.originalPackageName,
                              version: row.version,
                              appName: row.appName,
                            })}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-red-500/15 text-red-400 hover:bg-red-500/25 text-xs font-medium transition-colors"
                          >
                            <FiTrash2 /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            ))}
          </table>
        </div>
      </section>

      {/* Edit modal */}
      {editingApp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="card-chrome w-full max-w-lg p-0">
            <header className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
              <h3 className="inline-flex items-center gap-2 text-base font-semibold text-white">
                <FiEdit className="text-brand-400" /> Edit {editAppName}
              </h3>
              <button
                onClick={handleClearEditState}
                className="text-gray-500 hover:text-gray-200 p-1 rounded transition-colors"
                aria-label="Close"
              >
                ✕
              </button>
            </header>

            <div className="px-5 py-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1.5">App Name</label>
                  <input
                    type="text"
                    placeholder="e.g. MusicDJ"
                    value={editAppName}
                    onChange={(e) => setEditAppName(e.target.value)}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1.5">Package Name</label>
                  <input
                    type="text"
                    value={editPackageName}
                    onChange={(e) => setEditPackageName(e.target.value)}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1.5">Version Code</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    placeholder="e.g. 123456789"
                    value={editVersion}
                    onChange={(e) => setEditVersion(e.target.value)}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1.5">Version Name</label>
                  <input
                    type="text"
                    placeholder="e.g. 1.2.3"
                    value={editVersionName}
                    onChange={(e) => setEditVersionName(e.target.value)}
                    className="input-field"
                  />
                </div>
              </div>

              <div className="card-inset p-4 flex flex-col sm:flex-row gap-4 items-center">
                <div className="w-20 h-20 rounded-lg bg-white/[0.04] flex items-center justify-center shrink-0">
                  <IconPreview file={editImageFile} className="w-16 h-16 object-contain rounded" />
                </div>
                <div className="flex-1 w-full space-y-2">
                  <label className="btn-secondary cursor-pointer w-full">
                    <FiUpload /> Choose new .apk / .xapk
                    <input
                      type="file"
                      accept=".apk,.xapk"
                      onChange={handleEditApkFileChange}
                      className="hidden"
                    />
                  </label>
                  <label className="btn-secondary cursor-pointer w-full">
                    <FiImage /> Upload Icon
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setEditImageFile(e.target.files[0])}
                      className="hidden"
                    />
                  </label>
                  {editApkFile && !loadingEditMetadata && (
                    <div className="text-xs text-gray-400 space-y-0.5 pt-1">
                      <p>Selected: <span className="text-white font-medium">{editApkFile.name}</span></p>
                      <p>Size: <span className="text-white font-medium">{formatBytes(editApkSize)}</span></p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <footer className="px-5 py-4 border-t border-white/[0.06] flex justify-end gap-2">
              <button onClick={handleClearEditState} className="btn-secondary">Cancel</button>
              <button
                onClick={saveEditedApp}
                disabled={loadingEditMetadata || editUploading}
                className="btn-primary"
              >
                {editUploading ? 'Uploading…' : (loadingEditMetadata ? 'Extracting…' : 'Save')}
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* Single-version delete confirm */}
      {deleteCandidate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="card-chrome w-full max-w-md p-0">
            <header className="px-5 py-4 border-b border-white/[0.06]">
              <h3 className="inline-flex items-center gap-2 text-base font-semibold text-white">
                <FiTrash2 className="text-red-400" /> Delete version
              </h3>
            </header>
            <div className="px-5 py-4">
              <p className="text-sm text-gray-300">
                Delete version{' '}
                <span className="text-white font-semibold">{deleteCandidate.version}</span>{' '}
                of{' '}
                <span className="text-white font-semibold">{deleteCandidate.appName}</span>?
              </p>
              <p className="mt-2 text-xs text-gray-500">
                The .apk will be removed from Wasabi storage. This can't be undone.
              </p>
            </div>
            <footer className="px-5 py-4 border-t border-white/[0.06] flex justify-end gap-2">
              <button onClick={() => setDeleteCandidate(null)} className="btn-secondary">Cancel</button>
              <button
                onClick={async () => {
                  const app = deleteCandidate;
                  setDeleteCandidate(null);
                  await deleteVersion(app);
                }}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25 transition-colors"
              >
                <FiTrash2 /> Delete
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* Whole-app delete confirm */}
      {deleteAppCandidate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="card-chrome w-full max-w-md p-0">
            <header className="px-5 py-4 border-b border-white/[0.06]">
              <h3 className="inline-flex items-center gap-2 text-base font-semibold text-white">
                <FiTrash2 className="text-red-400" /> Delete entire app
              </h3>
            </header>
            <div className="px-5 py-4">
              <p className="text-sm text-gray-300">
                This removes <span className="text-white font-semibold">all versions</span> of{' '}
                <span className="text-white font-semibold">{deleteAppCandidate.appName}</span>.
              </p>
              <p className="mt-2 text-xs text-gray-500">
                Every .apk for this package will be deleted from Wasabi storage. This can't be undone.
              </p>
            </div>
            <footer className="px-5 py-4 border-t border-white/[0.06] flex justify-end gap-2">
              <button onClick={() => setDeleteAppCandidate(null)} className="btn-secondary">Cancel</button>
              <button
                onClick={async () => {
                  const app = deleteAppCandidate;
                  setDeleteAppCandidate(null);
                  await deletePackage(app);
                }}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25 transition-colors"
              >
                <FiTrash2 /> Delete app
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}


